import { useFrontChatPresent } from "hooks/useFrontChatPresent";
import { useFrontChatWindowVisible } from "hooks/useFrontChatWindowVisible";
import { useTenantId } from "hooks/useTenant";
import { useChatSurfaceCoordinator } from "providers/ChatSurfaceCoordinator";
import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { useScoutConversation } from "../hooks/useScoutConversation";
import { PopoverScout, type PopoverScoutDock } from "./PopoverScout";
import { ScoutFooterLauncher } from "./ScoutFooterLauncher";
import { useScoutMinimizedCompletionBadge } from "./useScoutMinimizedCompletionBadge";
import { useScoutPopoverSession } from "./useScoutPopoverSession";

// Both the popover and the minimized launcher dock to the same screen corner so
// the transition between modes feels like a state change in one element.
const DOCK: PopoverScoutDock = {
  corner: "bottom-left",
  offsetX: 40,
  offsetY: 40,
};

// Front's launcher is ~56px wide at left:20 of the viewport. When Front is
// loaded the Scout launcher shifts right by Front's full footprint + a 20px
// gap; when Front is absent Scout sits in Front's normal spot.
const LAUNCHER_LEFT_WITHOUT_FRONT = 20;
const LAUNCHER_LEFT_WITH_FRONT = 96;

// Front's iframe is pinned to 1201 in `FrontChat.tsx`. Scout normally sits
// above it (1250) so both launchers stay clickable; when Front's panel is
// open Scout drops behind so the support chat stays on top.
const SCOUT_LAUNCHER_Z_INDEX = 1250;
const SCOUT_LAUNCHER_Z_INDEX_BEHIND_FRONT = 1200;

// `/o/:tenantId/scout` and `/o/:tenantId/scout/:conversationId` — the
// full-page Scout view. The popover would be redundant (and visually
// awkward) on top of the page it mirrors.
const FULL_SCOUT_PAGE_REGEX = /^\/o\/[^/]+\/scout(\/|$)/;

type ViewMode = "closed" | "open" | "minimized";

/**
 * Fixed-position circular launcher mirroring Front's bottom-right chat
 * bubble. Lives at bottom-left of the viewport so the two launchers visually
 * pair up. Opens a Popover Scout conversation; supports a minimized state
 * that collapses back to the same launcher with spin and completion badge
 * affordances while the user works in the main view.
 */
export function ScoutSidebarFooterTrigger() {
  const [viewMode, setViewMode] = useState<ViewMode>("closed");
  const session = useScoutPopoverSession();
  const chatSurfaceCoordinator = useChatSurfaceCoordinator();
  const frontPresent = useFrontChatPresent();
  const frontWindowVisible = useFrontChatWindowVisible();
  const tenantId = useTenantId();
  const { pathname } = useLocation();
  const isMinimized = viewMode === "minimized";
  const launcherLeft = frontPresent ? LAUNCHER_LEFT_WITH_FRONT : LAUNCHER_LEFT_WITHOUT_FRONT;
  const launcherZIndex = frontWindowVisible
    ? SCOUT_LAUNCHER_Z_INDEX_BEHIND_FRONT
    : SCOUT_LAUNCHER_Z_INDEX;

  const handleDismissScoutSurface = useCallback(() => {
    setViewMode("closed");
  }, []);

  useEffect(() => {
    if (!chatSurfaceCoordinator) {
      return undefined;
    }
    return chatSurfaceCoordinator.registerDismissScout(handleDismissScoutSurface);
  }, [chatSurfaceCoordinator, handleDismissScoutSurface]);

  const { activeTurn, isSending } = useScoutConversation({
    tenantId,
    conversationId: session.conversationId,
    onConversationIdChange: session.setConversationId,
    // The footer only reads turn status for the spin/completion affordances.
    // The popover's own instance owns private-document fetching; enabling it
    // here would duplicate the network-only query and keep a redundant
    // private-document stream open.
    enablePrivateDocuments: false,
  });
  const { showCompletionBadge, clearCompletionBadge } = useScoutMinimizedCompletionBadge(
    isMinimized,
    activeTurn?.status
  );
  const isStreaming =
    isSending || activeTurn?.status === "starting" || activeTurn?.status === "streaming";

  const handleOpen = useCallback(() => {
    chatSurfaceCoordinator?.notifyScoutPopoverOpening();
    clearCompletionBadge();
    setViewMode("open");
  }, [chatSurfaceCoordinator, clearCompletionBadge]);
  const handleClose = useCallback(() => {
    clearCompletionBadge();
    setViewMode("closed");
    // X is an explicit "I'm done with this chat" — start fresh next time.
    // Minimize keeps the session; only this path resets it.
    session.reset();
  }, [clearCompletionBadge, session]);
  const handleDismissPopover = handleDismissScoutSurface;
  const handleMinimize = useCallback(() => setViewMode("minimized"), []);

  // Hide the launcher entirely when there's no usable tenant (Scout's API
  // requires one — without this the popover opens but `sendMessage` throws),
  // or when the user is already on the full Scout page.
  if (!tenantId || FULL_SCOUT_PAGE_REGEX.test(pathname)) {
    return null;
  }

  const showLauncher = viewMode === "closed" || viewMode === "minimized";

  return (
    <>
      {showLauncher ? (
        <ScoutFooterLauncher
          variant={isMinimized ? "minimized" : "closed"}
          onClick={handleOpen}
          launcherLeft={launcherLeft}
          launcherZIndex={launcherZIndex}
          isStreaming={isMinimized ? isStreaming : false}
          showCompletionBadge={isMinimized ? showCompletionBadge : false}
        />
      ) : null}
      <PopoverScout
        anchorEl={null}
        open={viewMode === "open"}
        onClose={handleClose}
        closeScout={handleDismissPopover}
        onMinimize={handleMinimize}
        session={session}
        dock={DOCK}
      />
    </>
  );
}
